/**
 * Decorative bootstrap particle filter for the site header.
 *
 * Self-installing and dependency-free: it renders only if a `.pf-header` canvas
 * exists, so removing the canvas removes the feature. Redraws on each filter
 * step (4/s) rather than per animation frame, which is what keeps it cheap.
 */

import {
  BETA_ENTER, BETA_EXIT, clampSumSq, divergenceReading, READING_SMOOTHING, smoothed,
} from './pf-divergence.ts'
import { DIGIT_MASKS, segmentOutlines } from './seven-segment.ts'
import { packState, unpackState, type PfState } from './pf-state.ts'
import {
  registerMeterActivation,
  WORLDLINE_COUNT_KEY,
  WORLDLINE_EVENT,
  WORLDLINE_KEY,
} from './theme-state.ts'


const STEPS = 20 // visible columns; the ring buffer holds exactly this many
// Enough particles to actually track a 3D chaotic system. At 44 the filter was
// worse than believing the raw observation: measured RMSE 5.70 against an
// observation noise of 4.0, with a nominal 90% credible interval covering the truth
// only 56% of the time. 120 brings that to RMSE 3.45 and 80% coverage. Particle
// count dominated every other knob tried, including a locally optimal proposal,
// which barely moved either number at 44 — the bottleneck there is sample
// impoverishment, not weight degeneracy.
const PARTICLES = 120
// Particles drawn per column. See the recording loop for why this is not PARTICLES.
const DRAWN = 44
const STEP_MS = 250 // 20 steps => a 5-second window
const ALPHA_BUCKETS = 6

// Guarded so the module can be imported by a test runner with no DOM. The
// serialiser below is pure and worth testing; the rest needs a browser.
const canvas =
  typeof document === 'undefined' ? null : document.querySelector<HTMLCanvasElement>('.pf-header')
const trigger =
  typeof document === 'undefined' ? null : document.querySelector<HTMLButtonElement>('.pf-header-trigger')
const ctx = canvas?.getContext('2d', { alpha: true }) ?? null

if (canvas && ctx) {
  // --- model state -------------------------------------------------------
  // Ring buffer: column c holds DRAWN positions and their normalised weights, plus
  // the true state at that step. Preallocated, never resized.
  const px = new Float32Array(STEPS * DRAWN)
  const pw = new Float32Array(STEPS * DRAWN)
  const truth = new Float32Array(STEPS)
  // The filter's output: the weighted mean of the particles at each step, plus
  // the 5th and 95th weighted percentiles, which bound a 90% credible interval.
  const est = new Float32Array(STEPS)
  const lo = new Float32Array(STEPS)
  const hi = new Float32Array(STEPS)
  // Scratch for the quantile sort: index order, reused every step, never grown.
  const order = new Int32Array(PARTICLES)
  const filled = { count: 0, head: 0 }

  // Live filter state. The Lorenz-63 state is 3D, so each particle carries all
  // three coordinates even though only x is observed and drawn.
  const sx = new Float32Array(PARTICLES)
  const sy = new Float32Array(PARTICLES)
  const sz = new Float32Array(PARTICLES)
  const weights = new Float32Array(PARTICLES)
  const rsx = new Float32Array(PARTICLES)
  const rsy = new Float32Array(PARTICLES)
  const rsz = new Float32Array(PARTICLES)

  let tx = -6
  let ty = -6
  let tz = 24
  let spare: number | null = null

  // Readout state. Declared with the model rather than beside the drawing code
  // because step() writes it and the pre-roll calls step() before a first draw.
  // The running average of sum-of-squared-weights, and the reading derived from it.
  // Smoothing happens BEFORE the map, not after: see ./pf-divergence.ts.
  let meanSumSq = 1 / PARTICLES
  let divergence = 0
  let readings = 0
  // Counts down over draws, dimming segments at random while positive. Fires only
  // on a worldline shift, never per resample: this filter resamples every step, so
  // a flicker per resample would just be a permanent flicker.
  let flicker = 0
  let beta = false

  const randn = (): number => {
    if (spare !== null) {
      const v = spare
      spare = null
      return v
    }
    let u = 0
    let v = 0
    let s = 0
    do {
      u = Math.random() * 2 - 1
      v = Math.random() * 2 - 1
      s = u * u + v * v
    } while (s === 0 || s >= 1)
    const m = Math.sqrt((-2 * Math.log(s)) / s)
    spare = v * m
    return u * m
  }

  // Started off the attractor and deliberately spread, so the opening frames show
  // the filter finding the trajectory rather than sitting on it.
  for (let i = 0; i < PARTICLES; i++) {
    sx[i] = tx + randn() * 3
    sy[i] = ty + randn() * 3
    sz[i] = tz + randn() * 3
    weights[i] = 1 / PARTICLES
  }

  // Lorenz-63 at the classic chaotic parameters.
  const SIGMA = 10
  const RHO = 28
  const BETA = 8 / 3

  // Rossler SRA1: a stochastic Runge-Kutta scheme for ADDITIVE noise, strong
  // order 1.5. Two drift evaluations and two Gaussians per component per step.
  //
  // Three schemes were tried before this one, and the reasoning matters:
  //
  //   Euler-Maruyama    strong order 1.0 here. Not the 0.5 usually quoted: that
  //                     figure is for multiplicative noise, and additive noise
  //                     lifts EM to 1.0. Measured 1.0-1.24 on this system.
  //   Milstein          reduces EXACTLY to Euler-Maruyama here. The diffusion
  //                     coefficient is constant, so its correction term vanishes,
  //                     which is why the obvious upgrade buys nothing.
  //   RK4 + noise split fourth order in the drift, but Lie-Trotter splitting caps
  //                     the scheme at strong order 1.0, so most of RK4's accuracy
  //                     is thrown away, at four drift evaluations per step.
  //
  // SRA1 beats all three: measured strong order 1.54-1.58 against a fixed Brownian
  // path, matching the theoretical 1.5, with error 18-35x smaller than EM at the
  // same step size and only two drift evaluations.
  //
  // Strong order is the relevant measure here, not weak: the particle paths are
  // being compared against one specific realised trajectory, not merely required
  // to have the right invariant distribution.
  const DT = 0.05
  const SUBSTEPS = 4
  const SQRT_DT = Math.sqrt(DT)
  const INV_SQRT3 = 1 / Math.sqrt(3)
  // Stochastic forcing, as in the stochastic Lorenz systems used as SMC
  // benchmarks. Without it every particle follows the same deterministic path
  // and the cloud has no spread to show.
  const PROC_SD = 1.6
  const OBS_SD = 4.0
  const INV_2S2 = 1 / (2 * OBS_SD * OBS_SD)
  // x runs to roughly +/-18 on this attractor. Scaled so a full lobe excursion
  // uses most of the strip's height rather than a thin band in the middle.
  const SCALE = 14

  // Scratch, so a step allocates nothing.
  const k1 = new Float32Array(3)
  const k2 = new Float32Array(3)
  const dW = new Float32Array(3)
  const chi2 = new Float32Array(3)

  /** Lorenz-63 drift into `out`. */
  function drift(x: number, y: number, z: number, out: Float32Array): void {
    out[0] = SIGMA * (y - x)
    out[1] = x * (RHO - z) - y
    out[2] = x * y - BETA * z
  }

  /** Advance one particle by SUBSTEPS steps of SRA1. */
  function advance(a: Float32Array, b: Float32Array, c: Float32Array, i: number): void {
    for (let step = 0; step < SUBSTEPS; step++) {
      const x = a[i]!
      const y = b[i]!
      const z = c[i]!

      // Two independent increments per component: dW drives the solution, and dZ
      // supplies the second one SRA1 needs to reach order 1.5. chi2 is the
      // combination that approximates the mixed time-noise integral I(1,0).
      for (let d = 0; d < 3; d++) {
        const w = SQRT_DT * randn()
        const zed = SQRT_DT * randn()
        dW[d] = w
        chi2[d] = 0.5 * (w + zed * INV_SQRT3)
      }

      drift(x, y, z, k1)

      // Stage 2, evaluated at 3/4 of the step and displaced by the noise term.
      const h1x = x + 0.75 * DT * k1[0]! + 1.5 * PROC_SD * chi2[0]!
      const h1y = y + 0.75 * DT * k1[1]! + 1.5 * PROC_SD * chi2[1]!
      const h1z = z + 0.75 * DT * k1[2]! + 1.5 * PROC_SD * chi2[2]!
      drift(h1x, h1y, h1z, k2)

      a[i] = x + DT * (k1[0]! / 3 + (2 * k2[0]!) / 3) + PROC_SD * dW[0]!
      b[i] = y + DT * (k1[1]! / 3 + (2 * k2[1]!) / 3) + PROC_SD * dW[1]!
      c[i] = z + DT * (k1[2]! / 3 + (2 * k2[2]!) / 3) + PROC_SD * dW[2]!
    }
  }

  // The truth follows the same dynamics, held in length-1 views so it can share
  // advance() rather than duplicating the integrator.
  const t1x = new Float32Array(1)
  const t1y = new Float32Array(1)
  const t1z = new Float32Array(1)

  function step(): void {
    // Truth: the same stochastic Lorenz system the filter is tracking.
    t1x[0] = tx
    t1y[0] = ty
    t1z[0] = tz
    advance(t1x, t1y, t1z, 0)
    tx = t1x[0]!
    ty = t1y[0]!
    tz = t1z[0]!

    // Only x is observed. y and z must be inferred from its history, which is
    // what makes this worth a particle filter.
    const obs = tx + OBS_SD * randn()

    for (let i = 0; i < PARTICLES; i++) advance(sx, sy, sz, i)

    let sum = 0
    for (let i = 0; i < PARTICLES; i++) {
      const d = obs - sx[i]!
      const w = Math.exp(-d * d * INV_2S2)
      weights[i] = w
      sum += w
    }
    if (!(sum > 0)) {
      for (let i = 0; i < PARTICLES; i++) weights[i] = 1 / PARTICLES
      sum = 1
    }

    // The filter's own quantities use every particle.
    let mean = 0
    let sumSq = 0
    for (let i = 0; i < PARTICLES; i++) {
      const w = weights[i]! / sum
      // Weighted mean must use the normalised weights BEFORE resampling —
      // afterwards they are all equal and this degenerates to a plain average.
      mean += (w * sx[i]!) / SCALE
      sumSq += w * w
    }

    // Record into the ring BEFORE resampling, so the cloud shows the weight
    // spread rather than a post-resample cloud of near-equal weights.
    //
    // Only a subsample is recorded. Particle count and cloud density are separate
    // concerns: the filter needs enough particles to track a 3D chaotic system,
    // while the strip is 40px tall and turns into a solid band well before that.
    // Drawing a fixed DRAWN of them keeps the visual and the draw cost as they were
    // while the estimate underneath improves. Index order is arbitrary with respect
    // to position, so a stride is an unbiased sample of the cloud.
    const base = filled.head * DRAWN
    let maxW = 0
    for (let d = 0; d < DRAWN; d++) {
      const i = Math.floor((d * PARTICLES) / DRAWN)
      const w = weights[i]! / sum
      px[base + d] = sx[i]! / SCALE
      pw[base + d] = w
      if (w > maxW) maxW = w
    }
    meanSumSq = smoothed(meanSumSq, sumSq, readings)
    readings++
    divergence = divergenceReading(meanSumSq, PARTICLES)
    // A sustained move across the ESS = N/2 line is a worldline shift, and the only
    // event the display reacts to. Hysteresis, so hovering on the line does not
    // chatter.
    const nowBeta = beta ? divergence >= BETA_EXIT : divergence >= BETA_ENTER
    if (nowBeta !== beta) flicker = 3
    beta = nowBeta
    // Normalise this column to its own max so the cloud never fades out.
    if (maxW > 0) {
      for (let d = 0; d < DRAWN; d++) pw[base + d]! /= maxW
    }
    truth[filled.head] = tx / SCALE
    est[filled.head] = mean

    // Weighted 5th and 95th percentiles: sort by position, then walk the
    // cumulative normalised weight. Must use pre-resample weights, like the
    // mean — after resampling every weight is 1/N and the band would collapse
    // to the plain particle range.
    for (let i = 0; i < PARTICLES; i++) order[i] = i
    // Insertion sort: PARTICLES is small and this allocates nothing, unlike
    // Array.prototype.sort with a comparator.
    for (let i = 1; i < PARTICLES; i++) {
      const k = order[i]!
      const kx = sx[k]!
      let j = i - 1
      while (j >= 0 && sx[order[j]!]! > kx) {
        order[j + 1] = order[j]!
        j--
      }
      order[j + 1] = k
    }
    let cum = 0
    let loSet = false
    lo[filled.head] = sx[order[0]!]! / SCALE
    hi[filled.head] = sx[order[PARTICLES - 1]!]! / SCALE
    for (let i = 0; i < PARTICLES; i++) {
      const idx = order[i]!
      cum += weights[idx]! / sum
      if (!loSet && cum >= 0.05) {
        lo[filled.head] = sx[idx]! / SCALE
        loSet = true
      }
      if (cum >= 0.95) {
        hi[filled.head] = sx[idx]! / SCALE
        break
      }
    }

    filled.head = (filled.head + 1) % STEPS
    if (filled.count < STEPS) filled.count++

    // Systematic resample
    const start = Math.random() / PARTICLES
    let acc = 0
    let j = 0
    for (let i = 0; i < PARTICLES; i++) {
      const target = start + i / PARTICLES
      while (acc < target && j < PARTICLES - 1) {
        acc += weights[j]! / sum
        j++
      }
      // All three coordinates travel together: resampling one without the others
      // would produce particles that are not points on any trajectory.
      rsx[i] = sx[j]!
      rsy[i] = sy[j]!
      rsz[i] = sz[j]!
    }
    sx.set(rsx)
    sy.set(rsy)
    sz.set(rsz)
  }

  // --- divergence readout -------------------------------------------------
  // A seven-segment display of how far the weights have diverged from uniform, in
  // the style of the Steins;Gate divergence meter. The reference is not merely
  // decorative. Because the average is taken over sumSq and only then mapped, the
  // reading is at or above 1 exactly when the time-averaged ESS is at or below N/2,
  // the conventional threshold for calling a particle filter degenerate. So the
  // leading digit is the worldline indicator and no separate marker is needed. See
  // ./pf-divergence.ts for the mapping, the smoothing, and the measurements.

  const DIGIT_W = 9
  const DIGIT_H = 16
  const DIGIT_GAP = 2
  const POINT_W = 3
  const THICK = 2
  const METER_PAD = 7
  // "d.dddddd": seven digits and a decimal point.
  const METER_W = 7 * DIGIT_W + POINT_W + 7 * DIGIT_GAP + 2 * METER_PAD
  // Below this the strip is too narrow to give up the width, so the trace keeps
  // all of it and the readout is not drawn at all.
  const METER_MIN_W = 300

  const SEGMENTS = segmentOutlines(DIGIT_W, DIGIT_H, THICK)

  function fillPoints(pts: number[], ox: number, oy: number): void {
    ctx!.beginPath()
    ctx!.moveTo(ox + pts[0]!, oy + pts[1]!)
    for (let k = 2; k < pts.length; k += 2) ctx!.lineTo(ox + pts[k]!, oy + pts[k + 1]!)
    ctx!.closePath()
    ctx!.fill()
  }

  /** One digit cell. Unlit segments stay faintly visible, as a real tube does. */
  function drawDigit(ox: number, oy: number, mask: number): void {
    for (let s = 0; s < 7; s++) {
      const lit = ((mask >> s) & 1) === 1
      // A flicker dims lit segments only. Unlit ones have nothing to lose, and
      // lighting them at random would spell impossible digits.
      const dim = lit && flicker > 0 && Math.random() < 0.4
      ctx!.globalAlpha = lit ? (dim ? 0.25 : 1) : 0.12
      fillPoints(SEGMENTS[s]!, ox, oy)
    }
  }

  /** Right-aligned in the strip, vertically centred. */
  function drawMeter(right: number): void {
    const text = divergence.toFixed(6)
    ctx!.fillStyle = themed('--meter', '#d98324')
    let ox = right - METER_W + METER_PAD
    const oy = (h - DIGIT_H) / 2
    for (const ch of text) {
      if (ch === '.') {
        ctx!.globalAlpha = 1
        ctx!.beginPath()
        ctx!.arc(ox + POINT_W / 2, oy + DIGIT_H - 1.5, THICK / 2, 0, 6.283185307179586)
        ctx!.fill()
        ox += POINT_W + DIGIT_GAP
        continue
      }
      drawDigit(ox, oy, DIGIT_MASKS[ch.charCodeAt(0) - 48]!)
      ox += DIGIT_W + DIGIT_GAP
    }
    ctx!.globalAlpha = 1
    if (flicker > 0) flicker--
  }

  // --- rendering ---------------------------------------------------------
  let w = 0
  let h = 0

  function resize(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    const rect = canvas!.getBoundingClientRect()
    // Keep w/h as the exact CSS box size (not rounded here): the backing
    // buffer is derived from it below, and toY()/colW draw against it. If
    // this were rounded first, the drawing space and the actual rendered
    // box would disagree by a fraction of a device pixel and slowly drift
    // as the layout is measured at different fractional widths.
    w = Math.max(1, rect.width)
    h = Math.max(1, rect.height)
    canvas!.width = Math.max(1, Math.round(w * dpr))
    canvas!.height = Math.max(1, Math.round(h * dpr))
    ctx!.setTransform(dpr, 0, 0, dpr, 0, 0)
  }

  /** Read a themed colour off the canvas, so both lines follow light/dark. */
  function themed(name: string, fallback: string): string {
    const v = getComputedStyle(canvas!).getPropertyValue(name).trim()
    return v || fallback
  }

  /** Read the CSS accent colour so the animation follows the site theme. */
  function accent(): string {
    const v = getComputedStyle(canvas!).getPropertyValue('--accent').trim()
    return v || '#6534d9'
  }

  const toY = (x: number): number => h / 2 - Math.max(-1.4, Math.min(1.4, x)) * (h / 2 - 4) / 1.4

  function draw(): void {
    ctx!.clearRect(0, 0, w, h)
    if (filled.count === 0) return

    // The readout takes the right-hand end and the trace keeps the rest, so the
    // strip reads as one instrument. On a narrow strip the trace keeps everything.
    const meter = w >= METER_MIN_W
    const traceW = meter ? w - METER_W : w
    if (meter) drawMeter(w)

    const colW = traceW / STEPS
    const dotW = Math.max(1.5, Math.min(3.5, colW * 0.45))
    const colour = accent()
    const muted = themed('--fg-muted', '#6b645c')

    // Oldest visible column first, so time reads left to right.
    const oldest = filled.count < STEPS ? 0 : filled.head

    // The 90% credible band, drawn first so the cloud and lines read on top.
    if (filled.count > 1) {
      ctx!.globalAlpha = 0.22
      ctx!.fillStyle = colour
      ctx!.beginPath()
      for (let c = 0; c < filled.count; c++) {
        const idx = (oldest + c) % STEPS
        const cx = c * colW + colW / 2
        const y = toY(hi[idx]!)
        if (c === 0) ctx!.moveTo(cx, y)
        else ctx!.lineTo(cx, y)
      }
      // Back along the lower edge to close the ribbon.
      for (let c = filled.count - 1; c >= 0; c--) {
        const idx = (oldest + c) % STEPS
        ctx!.lineTo(c * colW + colW / 2, toY(lo[idx]!))
      }
      ctx!.closePath()
      ctx!.fill()
      ctx!.globalAlpha = 1
    }

    // Bucket by alpha: one fillStyle write per bucket, not per particle.
    for (let b = ALPHA_BUCKETS; b >= 1; b--) {
      const lo = (b - 1) / ALPHA_BUCKETS
      const hi = b / ALPHA_BUCKETS
      ctx!.fillStyle = colour
      ctx!.globalAlpha = 0.05 + 0.62 * (b / ALPHA_BUCKETS) ** 1.6
      for (let c = 0; c < filled.count; c++) {
        const idx = (oldest + c) % STEPS
        const base = idx * DRAWN
        const cx = c * colW + colW / 2
        for (let i = 0; i < DRAWN; i++) {
          const wt = pw[base + i]!
          if (wt < lo || wt >= hi) continue
          ctx!.beginPath()
          ctx!.arc(cx, toY(px[base + i]!), dotW / 2, 0, 6.283185307179586)
          ctx!.fill()
        }
      }
    }

    // Two lines, deliberately distinguishable: the true state dashed and muted
    // as the reference, the filter's estimate solid and in the accent colour as
    // the result. Truth first, so the estimate reads on top where they meet.
    const path = (values: Float32Array): void => {
      ctx!.beginPath()
      for (let c = 0; c < filled.count; c++) {
        const idx = (oldest + c) % STEPS
        const cx = c * colW + colW / 2
        const cy = toY(values[idx]!)
        if (c === 0) ctx!.moveTo(cx, cy)
        else ctx!.lineTo(cx, cy)
      }
      ctx!.stroke()
    }

    ctx!.globalAlpha = 0.85
    ctx!.strokeStyle = muted
    ctx!.lineJoin = 'round'
    ctx!.lineCap = 'butt'
    ctx!.lineWidth = 1.25
    ctx!.setLineDash([3, 2.5])
    path(truth)
    ctx!.setLineDash([])

    ctx!.globalAlpha = 1
    ctx!.strokeStyle = colour
    ctx!.lineJoin = 'round'
    ctx!.lineCap = 'round'
    ctx!.lineCap = 'round'
    ctx!.lineWidth = 1.75
    path(est)
    ctx!.globalAlpha = 1
  }

  // --- scheduling --------------------------------------------------------
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)')
  let timer: number | null = null
  let visible = true

  function tick(): void {
    step()
    draw()
  }

  function start(): void {
    if (timer !== null || reduced.matches || !visible) return
    timer = window.setInterval(tick, STEP_MS)
  }

  function stop(): void {
    if (timer === null) return
    window.clearInterval(timer)
    timer = null
  }

  function sync(): void {
    if (reduced.matches || document.hidden || !visible) stop()
    else start()
  }

  // --- persistence across navigations ------------------------------------
  // Every page change on a static site is a full load, so the filter would
  // otherwise restart from scratch on every click. Snapshot into sessionStorage
  // on the way out and restore on the way in, so the strip is continuous for a
  // whole visit and forgotten when the tab closes.
  const KEY = 'pf-header-state'

  /** A view onto the live arrays, so pack/unpack read and write them directly. */
  const view = (): PfState => ({
    px, pw, truth, est, lo, hi, sx, sy, sz, tx, ty, tz,
    head: filled.head, count: filled.count, sumSq: meanSumSq,
  })

  function save(): void {
    try {
      sessionStorage.setItem(KEY, packState(view()))
    } catch {
      // Storage blocked or full. The animation just starts fresh next page.
    }
  }

  function restore(): boolean {
    let raw: string | null = null
    try {
      raw = sessionStorage.getItem(KEY)
    } catch {
      return false
    }
    if (raw === null) return false
    const s = view()
    if (!unpackState(raw, s)) return false
    tx = s.tx
    ty = s.ty
    tz = s.tz
    filled.head = s.head
    filled.count = s.count
    meanSumSq = clampSumSq(s.sumSq, PARTICLES)
    divergence = divergenceReading(meanSumSq, PARTICLES)
    // Past the running-mean warmup, so a continued visit keeps smoothing at the
    // constant rate instead of jumping to the next reading.
    readings = Math.ceil(1 / READING_SMOOTHING)
    // Adopt the restored field without flickering: continuing a visit is not a
    // worldline shift.
    beta = divergence >= 1
    return true
  }

  // pagehide is the reliable one: beforeunload does not fire dependably on
  // mobile Safari, and visibilitychange covers the tab-switch case.
  window.addEventListener('pagehide', save)
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) save()
  })

  // A reload means start over. Navigating between pages continues the filter,
  // which is the whole point of persisting it, but refreshing should give a fresh
  // one — that is what a reader pressing refresh is asking for.
  //
  // Note this cannot distinguish a hard refresh from a soft one: the browser
  // exposes the navigation TYPE but not whether the cache was bypassed, and a
  // page cannot read its own request headers. Both kinds of reload reset.
  const navEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
  if (navEntry?.type === 'reload') {
    try {
      sessionStorage.removeItem(KEY)
    } catch {
      // Storage blocked. restore() will find nothing either way.
    }
  }

  const restored = restore()
  // Observable so the behaviour can be asserted. Pixels cannot distinguish
  // "restored" from "restored then stepped once", because a step fires every
  // 250ms and each one consumes randomness.
  canvas.dataset.pfRestored = restored ? '1' : '0'

  resize()
  window.addEventListener('resize', () => {
    resize()
    draw()
  })
  document.addEventListener('visibilitychange', sync)
  reduced.addEventListener('change', () => {
    stop()
    if (reduced.matches) {
      // Static: one pre-rolled window, drawn once.
      for (let i = 0; i < STEPS; i++) step()
      draw()
    } else sync()
  })

  // Stop entirely once scrolled past, which is most of a reader's session.
  new IntersectionObserver(
    (entries) => {
      visible = entries[0]?.isIntersecting ?? true
      sync()
    },
    { rootMargin: '32px' },
  ).observe(canvas)

  // Pre-roll only on a genuinely fresh start. A restored snapshot already holds
  // a full window, and rolling it again would jump the animation forward by 20
  // steps on every single page change — the opposite of continuity.
  if (!restored) for (let i = 0; i < STEPS; i++) step()
  draw()
  if (!reduced.matches) sync()
}

let memoryCount = 0
let memoryUnlocked = false
let sessionStorageAvailable = true

const readUnlocked = (): boolean => {
  if (memoryUnlocked) return true
  try { return localStorage.getItem(WORLDLINE_KEY) === '1' } catch { return false }
}

const readCount = (): number => {
  if (!sessionStorageAvailable) return memoryCount
  try {
    return Number(sessionStorage.getItem(WORLDLINE_COUNT_KEY) ?? 0) || 0
  } catch {
    sessionStorageAvailable = false
    return memoryCount
  }
}

const writeCount = (count: number): void => {
  memoryCount = count
  if (!sessionStorageAvailable) return
  try {
    if (count === 0) sessionStorage.removeItem(WORLDLINE_COUNT_KEY)
    else sessionStorage.setItem(WORLDLINE_COUNT_KEY, String(count))
  } catch {
    sessionStorageAvailable = false
  }
}

trigger?.addEventListener('click', () => {
  const next = registerMeterActivation(readCount(), readUnlocked())
  writeCount(next.count)
  if (next.unlock) {
    memoryUnlocked = true
    window.dispatchEvent(new CustomEvent(WORLDLINE_EVENT))
  }
})
